import useAuth from 'hooks/useAuth';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MfaEnrolment } from 'sections/auth/mfa/MfaEnrolment';
import { cognitoUser } from 'utils/cognitoUser';
import { t } from 'utils/getTranslations';

/**
 * Enrolment demanded at sign-in: the backend refused the portal session until an authenticator exists,
 * so this runs against the Cognito session `cognitoUser` still holds and finishes the sign-in itself.
 */
export function AuthMFASetup() {
    const { completeMfaSetup, discardCognitoSession } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const signedIn = useRef(false);
    // Read once: finishing the sign-in signs the Cognito session out, and the confirmation screen
    // must still render afterwards.
    const [session] = useState(() => cognitoUser.getCognitoUser());
    const [email] = useState(() => cognitoUser.getSignInEmail() ?? session?.getUsername() ?? '');
    const [tenantName] = useState(() => cognitoUser.getMfaSetupTenantName());

    // Leaving the page before the enrolment finishes drops the Cognito tokens the refused sign-in left in
    // browser storage; a finished sign-in keeps them like any other.
    useEffect(() => {
        const dropUnfinishedSession = () => {
            if (!signedIn.current) {
                discardCognitoSession();
            }
        };
        window.addEventListener('pagehide', dropUnfinishedSession);
        return () => window.removeEventListener('pagehide', dropUnfinishedSession);
    }, [discardCognitoSession]);

    // Nobody lands here without a Cognito session to enrol against; a refresh loses it.
    useEffect(() => {
        if (!session) {
            navigate('/login', { replace: true });
        }
    }, [session, navigate]);

    if (!session) {
        return null;
    }

    return (
        <MfaEnrolment
            email={email}
            tenantName={tenantName}
            onEnrolled={async () => {
                await completeMfaSetup();
                signedIn.current = true;
            }}
            continueLabel={t('auth.mfa-continue-to-portal')}
            onContinue={() => {
                cognitoUser.clear();
                // The login page's guard sends a signed-in user to the return link or the landing page.
                navigate('/login', { replace: true, state: location.state as unknown });
            }}
            cancelLabel={t('auth.mfa-sign-out')}
            onCancel={() => {
                discardCognitoSession();
                navigate('/login', { replace: true });
            }}
        />
    );
}
